import { ethers } from 'ethers';

export const LAUNCHPAD_TRADE_INTENT_VERSION = 1;
export const LAUNCHPAD_RESOLUTION_VERSION = 1;
export const LAUNCHPAD_EXECUTION_PLAN_VERSION = 1;

const LAUNCHPAD_CHAIN_ID = 56;
const LAUNCHPAD_CHAIN_KEY = 'bsc';
const LAUNCHPAD_SIDES = new Set(['buy', 'sell']);
const AMOUNT_MODES = new Set(['exactInput', 'exactOutput']);
const TIMING_MODES = new Set(['immediate', 'frontrun', 'backrun', 'confirmed']);
const QUOTE_MODES = new Set(['auto', 'native', 'token']);
const RESOLUTION_PROTOCOLS = new Set([
  'unresolved',
  'flap',
  'fourmeme_v1',
  'fourmeme_v2',
  'pancake_v2',
  'pancake_v3',
  'universal',
  'ordinary_dex',
]);
const RESOLUTION_PHASES = new Set([
  'unresolved',
  'flap_internal',
  'fourmeme_v1_internal',
  'fourmeme_v2_internal',
  'pancake_v2_external',
  'pancake_v3_external',
  'universal_external',
  'external',
  'restricted',
  'unsupported',
]);
const EXECUTABLE_PHASE_BY_PROTOCOL = Object.freeze({
  flap: 'flap_internal',
  fourmeme_v1: 'fourmeme_v1_internal',
  fourmeme_v2: 'fourmeme_v2_internal',
  pancake_v2: 'pancake_v2_external',
  pancake_v3: 'pancake_v3_external',
  universal: 'universal_external',
  ordinary_dex: 'external',
});

const asString = (value, fallback = '') => (
  value === undefined || value === null ? fallback : String(value)
);

const optionalAddress = (value, label) => {
  const address = asString(value).trim();
  if (!address) return '';
  if (!ethers.isAddress(address)) throw new Error(`${label} address is invalid`);
  return address;
};

const requiredAddress = (value, label) => {
  const address = optionalAddress(value, label);
  if (!address) throw new Error(`${label} address is required`);
  return address;
};

const normalizeChain = (chain = {}) => ({
  key: asString(chain.key).trim().toLowerCase(),
  id: Number(chain.id || 0),
  name: asString(chain.name),
});

const normalizeAmount = (amount = {}, kind) => {
  const mode = amount.mode || (kind === 'ordinary_dex' ? 'transaction' : '');
  if (kind === 'launchpad' && !AMOUNT_MODES.has(mode)) {
    throw new Error('Launchpad amount mode must be exactInput or exactOutput');
  }
  return {
    mode,
    value: asString(amount.value, '0'),
    minOutput: asString(amount.minOutput),
    maxInput: asString(amount.maxInput),
  };
};

const normalizeQuotePreference = (preference = {}) => {
  const mode = preference.mode || 'auto';
  if (!QUOTE_MODES.has(mode)) throw new Error('Quote preference mode is invalid');
  return {
    mode,
    token: mode === 'token' ? requiredAddress(preference.token, 'Quote token') : '',
  };
};

const normalizeTiming = (timing = {}) => {
  const mode = timing.mode || 'immediate';
  if (!TIMING_MODES.has(mode)) throw new Error('Trade timing mode is invalid');
  const normalized = { mode };
  if (timing.targetPosition !== undefined && timing.targetPosition !== null) {
    normalized.targetPosition = Number(timing.targetPosition);
  }
  return normalized;
};

const normalizeChecks = (checks = {}) => ({
  burn: Boolean(checks.burn),
  tax: Boolean(checks.tax),
  sameBlockSell: Boolean(checks.sameBlockSell),
  maxBurnBps: Number(checks.maxBurnBps || 0),
  maxTaxBps: Number(checks.maxTaxBps || 0),
});

const normalizeGasPolicy = (gasPolicy = {}) => {
  const normalized = { mode: asString(gasPolicy.mode, 'configured') };
  if (gasPolicy.multiplierBps !== undefined && gasPolicy.multiplierBps !== null) {
    normalized.multiplierBps = Number(gasPolicy.multiplierBps);
  }
  if (gasPolicy.gasPrice !== undefined && gasPolicy.gasPrice !== null) {
    normalized.gasPrice = asString(gasPolicy.gasPrice);
  }
  if (gasPolicy.maxFeePerGas !== undefined && gasPolicy.maxFeePerGas !== null) {
    normalized.maxFeePerGas = asString(gasPolicy.maxFeePerGas);
  }
  if (gasPolicy.maxPriorityFeePerGas !== undefined && gasPolicy.maxPriorityFeePerGas !== null) {
    normalized.maxPriorityFeePerGas = asString(gasPolicy.maxPriorityFeePerGas);
  }
  return normalized;
};

const normalizeTargetBinding = (targetBinding) => {
  if (!targetBinding) return null;
  const hash = asString(targetBinding.hash).trim();
  if (hash && !/^0x[0-9a-fA-F]{64}$/.test(hash)) throw new Error('Target transaction hash is invalid');
  const normalized = {
    hash,
    from: optionalAddress(targetBinding.from, 'Target wallet'),
  };
  if (targetBinding.nonce !== undefined && targetBinding.nonce !== null) {
    normalized.nonce = Number(targetBinding.nonce);
  }
  return normalized;
};

const normalizeTradeIntent = (input = {}, kind = 'launchpad') => {
  const version = Number(input.version ?? LAUNCHPAD_TRADE_INTENT_VERSION);
  if (version !== LAUNCHPAD_TRADE_INTENT_VERSION) throw new Error('Unsupported trade intent version');
  const chain = normalizeChain(input.chain);
  if (!chain.key || !chain.id) throw new Error('Trade chain is required');
  if (kind === 'launchpad' && (chain.key !== LAUNCHPAD_CHAIN_KEY || chain.id !== LAUNCHPAD_CHAIN_ID)) {
    throw new Error('Launchpad trading is BSC only');
  }
  const side = input.side || (kind === 'ordinary_dex' ? 'unknown' : '');
  if (kind === 'launchpad' && !LAUNCHPAD_SIDES.has(side)) throw new Error('Launchpad trade side is invalid');
  return {
    version,
    kind,
    chain,
    side,
    amount: normalizeAmount(input.amount, kind),
    token: kind === 'launchpad'
      ? requiredAddress(input.token, 'Target token')
      : optionalAddress(input.token, 'Target token'),
    quotePreference: normalizeQuotePreference(input.quotePreference),
    timing: normalizeTiming(input.timing),
    checks: normalizeChecks(input.checks),
    gasPolicy: normalizeGasPolicy(input.gasPolicy),
    wallet: { address: kind === 'launchpad'
      ? requiredAddress(input.wallet?.address, 'Execution wallet')
      : optionalAddress(input.wallet?.address, 'Execution wallet') },
    targetBinding: normalizeTargetBinding(input.targetBinding),
  };
};

export const normalizeLaunchpadTradeIntent = (input) => normalizeTradeIntent(input, 'launchpad');

const normalizeApproval = (approval = {}) => ({
  token: requiredAddress(approval.token, 'Approval token'),
  spender: requiredAddress(approval.spender, 'Approval spender'),
  amount: asString(approval.amount),
  kind: asString(approval.kind, 'erc20'),
});

const normalizeStateCheck = (check = {}) => {
  const data = asString(check.data).trim();
  const expectedResultHash = asString(check.expectedResultHash).trim();
  if (!/^0x[0-9a-fA-F]*$/.test(data) || data.length % 2 !== 0) {
    throw new Error('Launchpad state-check calldata is invalid');
  }
  if (!/^0x[0-9a-fA-F]{64}$/.test(expectedResultHash)) {
    throw new Error('Launchpad state-check result hash is invalid');
  }
  return {
    to: requiredAddress(check.to, 'Launchpad state-check target'),
    data: data.toLowerCase(),
    expectedResultHash: expectedResultHash.toLowerCase(),
  };
};

const BLOCKING_LAUNCHPAD_RESTRICTIONS = new Set([
  'fourmeme_extended_state_unavailable',
  'trader_input_requires_detected_trade',
]);

export const getLaunchpadBlockingRestrictions = (resolution = {}) => (
  Array.isArray(resolution?.restrictions)
    ? resolution.restrictions.filter((restriction) => BLOCKING_LAUNCHPAD_RESTRICTIONS.has(restriction))
    : []
);

export const isLaunchpadResolutionExecutable = (resolution = {}) => {
  const executablePhase = EXECUTABLE_PHASE_BY_PROTOCOL[resolution?.protocol];
  return Boolean(executablePhase)
    && resolution?.phase === executablePhase
    && resolution?.executable !== false
    && getLaunchpadBlockingRestrictions(resolution).length === 0;
};

export const createLaunchpadResolution = (input = {}) => {
  const version = Number(input.version ?? LAUNCHPAD_RESOLUTION_VERSION);
  if (version !== LAUNCHPAD_RESOLUTION_VERSION) throw new Error('Unsupported launchpad resolution version');
  const protocol = asString(input.protocol, 'unresolved');
  const phase = asString(input.phase, 'unresolved');
  if (!RESOLUTION_PROTOCOLS.has(protocol)) throw new Error('Launchpad resolution protocol is invalid');
  if (!RESOLUTION_PHASES.has(phase)) throw new Error('Launchpad resolution phase is invalid');
  return {
    version,
    protocol,
    phase,
    manager: optionalAddress(input.manager, 'Launchpad manager'),
    router: optionalAddress(input.router, 'DEX router'),
    quoteAsset: {
      type: asString(input.quoteAsset?.type, 'unknown'),
      address: optionalAddress(input.quoteAsset?.address, 'Quote asset'),
      symbol: asString(input.quoteAsset?.symbol),
      decimals: Number(input.quoteAsset?.decimals || 0),
    },
    pool: optionalAddress(input.pool, 'Liquidity pool'),
    path: Array.isArray(input.path) ? input.path.map((address) => requiredAddress(address, 'Trade path token')) : [],
    approvals: Array.isArray(input.approvals) ? input.approvals.map(normalizeApproval) : [],
    fees: Array.isArray(input.fees) ? input.fees.map((fee) => ({ ...fee })) : [],
    stateChecks: Array.isArray(input.stateChecks) ? input.stateChecks.map(normalizeStateCheck) : [],
    capabilities: { ...(input.capabilities || {}) },
    restrictions: Array.isArray(input.restrictions) ? [...input.restrictions] : [],
    discoverySource: asString(input.discoverySource),
    authoritativeQuote: optionalAddress(input.authoritativeQuote, 'Authoritative quote'),
    settlementKind: asString(input.settlementKind),
    settlementToken: optionalAddress(input.settlementToken, 'Settlement token'),
    routeMode: asString(input.routeMode),
    routeIdentity: asString(input.routeIdentity),
    routeStages: Array.isArray(input.routeStages) ? input.routeStages.map(asString) : [],
  };
};

const comparableQuantity = (value) => {
  if (value === undefined || value === null || value === '') return '';
  try {
    return ethers.toBigInt(value).toString();
  } catch (_) {
    return String(value);
  }
};

const comparableTransaction = (transaction = {}) => ({
  to: asString(transaction.to).toLowerCase(),
  from: asString(transaction.from).toLowerCase(),
  data: asString(transaction.data, '0x').toLowerCase(),
  value: comparableQuantity(transaction.value),
  gas: comparableQuantity(transaction.gas || transaction.gasLimit),
  gasLimit: comparableQuantity(transaction.gasLimit || transaction.gas),
  gasPrice: comparableQuantity(transaction.gasPrice),
  maxFeePerGas: comparableQuantity(transaction.maxFeePerGas),
  maxPriorityFeePerGas: comparableQuantity(transaction.maxPriorityFeePerGas),
  nonce: comparableQuantity(transaction.nonce),
  chainId: comparableQuantity(transaction.chainId),
  type: comparableQuantity(transaction.type),
});

const transactionsMatch = (left, right) => (
  JSON.stringify(comparableTransaction(left)) === JSON.stringify(comparableTransaction(right))
);

const normalizedObjectsMatch = (left, right) => JSON.stringify(left) === JSON.stringify(right);

export const buildLaunchpadExecutionPlan = ({
  intent,
  resolution,
  transaction,
  preparedStateVersion = '',
  executionKind = 'direct',
  executor = '',
} = {}) => {
  if (!intent || intent.version !== LAUNCHPAD_TRADE_INTENT_VERSION) throw new Error('Normalized trade intent is required');
  if (!resolution || resolution.version !== LAUNCHPAD_RESOLUTION_VERSION) throw new Error('Normalized trade resolution is required');
  if (!transaction || typeof transaction !== 'object') throw new Error('Execution transaction is required');
  if (!['direct', 'proxy'].includes(executionKind)) throw new Error('Execution kind is invalid');
  const executablePhase = EXECUTABLE_PHASE_BY_PROTOCOL[resolution.protocol];
  if (!executablePhase) throw new Error('Launchpad resolution is not executable');
  if (resolution.phase !== executablePhase) {
    throw new Error('Launchpad resolution protocol/phase combination is invalid');
  }
  const blockingRestrictions = getLaunchpadBlockingRestrictions(resolution);
  if (blockingRestrictions.length > 0 || resolution.executable === false) {
    throw new Error(`Launchpad resolution is blocked: ${blockingRestrictions.join(', ') || 'not executable'}`);
  }
  const transactionExecutor = requiredAddress(transaction.to, 'Execution transaction target');
  const normalizedExecutor = optionalAddress(executor, 'Execution contract') || transactionExecutor;
  if (normalizedExecutor.toLowerCase() !== transactionExecutor.toLowerCase()) {
    throw new Error('Execution contract does not match transaction target');
  }
  const directTarget = resolution.manager || resolution.router;
  if (executionKind === 'direct' && (!directTarget || directTarget.toLowerCase() !== normalizedExecutor.toLowerCase())) {
    throw new Error('Launchpad direct executor does not match the resolved manager or router');
  }
  if (intent.kind === 'launchpad' && !transaction.from) {
    throw new Error('Launchpad execution transaction sender is required');
  }
  if (transaction.from && intent.wallet?.address
    && requiredAddress(transaction.from, 'Execution transaction sender').toLowerCase()
      !== intent.wallet.address.toLowerCase()) {
    throw new Error('Execution transaction wallet does not match trade intent');
  }
  return {
    version: LAUNCHPAD_EXECUTION_PLAN_VERSION,
    intentVersion: intent.version,
    intent,
    resolution,
    execution: {
      kind: executionKind,
      executor: normalizedExecutor,
      transaction,
    },
    stateChecks: Array.isArray(resolution.stateChecks) ? resolution.stateChecks.map(normalizeStateCheck) : [],
    preparedStateVersion: asString(preparedStateVersion),
  };
};

export const adaptLegacyDexTrade = ({
  chainKey,
  chainId,
  chainName = '',
  transaction,
  walletAddress = '',
  side = 'unknown',
  token = '',
} = {}) => {
  if (!transaction || typeof transaction !== 'object') throw new Error('Legacy DEX transaction is required');
  const router = optionalAddress(transaction.to, 'DEX router');
  const intent = normalizeTradeIntent({
    chain: { key: chainKey, id: chainId, name: chainName },
    side,
    amount: { mode: 'transaction', value: asString(transaction.value, '0') },
    token,
    quotePreference: { mode: 'auto' },
    timing: { mode: 'immediate' },
    checks: {},
    gasPolicy: { mode: 'configured' },
    wallet: { address: walletAddress },
  }, 'ordinary_dex');
  const resolution = createLaunchpadResolution({
    protocol: 'ordinary_dex',
    phase: 'external',
    router,
  });
  return {
    intent,
    resolution,
    executionPlan: buildLaunchpadExecutionPlan({ intent, resolution, transaction }),
  };
};

export const createTradeExecutionEnvelope = ({
  tradeIntent,
  resolution,
  executionPlan,
  chainKey,
  chainId,
  chainName = '',
  transaction,
  walletAddress = '',
} = {}) => {
  const hasExpandedEnvelope = Boolean(tradeIntent || resolution || executionPlan);
  if (!hasExpandedEnvelope) {
    return adaptLegacyDexTrade({ chainKey, chainId, chainName, transaction, walletAddress });
  }
  if (!tradeIntent || !resolution || !executionPlan) {
    throw new Error('Trade intent, resolution, and execution plan must be provided together');
  }
  const intent = tradeIntent.kind === 'ordinary_dex'
    ? normalizeTradeIntent(tradeIntent, 'ordinary_dex')
    : normalizeLaunchpadTradeIntent(tradeIntent);
  const normalizedResolution = createLaunchpadResolution(resolution);
  if (Number(executionPlan.version) !== LAUNCHPAD_EXECUTION_PLAN_VERSION
    || Number(executionPlan.intentVersion) !== intent.version) {
    throw new Error('Execution plan version does not match trade intent');
  }
  const planIntent = executionPlan.intent?.kind === 'ordinary_dex'
    ? normalizeTradeIntent(executionPlan.intent, 'ordinary_dex')
    : normalizeLaunchpadTradeIntent(executionPlan.intent);
  const planResolution = createLaunchpadResolution(executionPlan.resolution);
  if (!normalizedObjectsMatch(planIntent, intent)) {
    throw new Error('Execution plan intent does not match trade intent');
  }
  if (!normalizedObjectsMatch(planResolution, normalizedResolution)) {
    throw new Error('Execution plan resolution does not match trade resolution');
  }
  const planTransaction = executionPlan.execution?.transaction;
  if (!planTransaction) throw new Error('Execution plan transaction is required');
  const selectedTransaction = transaction || planTransaction;
  if (transaction && !transactionsMatch(transaction, planTransaction)) {
    throw new Error('Transaction request does not match execution plan');
  }
  const normalizedPlan = buildLaunchpadExecutionPlan({
    intent,
    resolution: normalizedResolution,
    transaction: selectedTransaction,
    preparedStateVersion: executionPlan.preparedStateVersion,
    executionKind: executionPlan.execution?.kind,
    executor: executionPlan.execution?.executor,
  });
  return { intent, resolution: normalizedResolution, executionPlan: normalizedPlan };
};
